import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14 } from "styled-components";
import React from "react";
import { gsap } from "gsap";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Media } from "~/features/ui/media";
import { Headline } from "~/features/ui/headline";
import { IconButton } from "~/features/ui/icon-button";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { RichText } from "~/features/ui/rich-text";
import { VideoControls } from "~/features/ui/video/video-controls";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
import Plus from "~/public/icons/plus.svg";
export const mediaGridContentImageCardFragment = graphql(`
  fragment MediaGridContentImageCard on blocks_contentImageCard_BlockType {
    media: baseMedia {
      ...Media
    }
    title: baseText
    description: baseRichText
    link: baseLink {
      ...Link
      linkText
    }
    supportLink: baseLink2 {
      ...Link
    }
  }
`);
type CardProps = FragmentType<typeof mediaGridContentImageCardFragment> & {
  colSpan?: 3 | 4 | 6 | 8 | 12;
  featured?: boolean;
};
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
export type MediaGridContentImageCardViewProps = {
  colSpan?: 3 | 4 | 6 | 8 | 12;
  description?: React.ReactNode;
  featured?: boolean;
  link?: PlainLink | null;
  media?: (videoRef: React.RefObject<HTMLVideoElement | null>) => React.ReactNode;
  supportLink?: PlainLink | null;
  title?: string | null;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export function MediaGridContentImageCardView({
  colSpan = 6,
  description,
  featured,
  link: linkItem,
  media,
  supportLink: supportLinkItem,
  title
}: MediaGridContentImageCardViewProps) {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const hoverContentRef = React.useRef<HTMLDivElement>(null);
  const activeTweenRef = React.useRef<gsap.core.Timeline | null>(null);
  const [isVideo, setIsVideo] = React.useState(false);
  const icon = getPlainLinkIcon(linkItem);
  const supportIcon = getPlainLinkIcon(supportLinkItem);
  const hasLink = isLinkValid(linkItem);
  const hasSupportLink = isLinkValid(supportLinkItem);
  const hasAnyLink = hasLink || hasSupportLink;
  // The whole card is the link. A support link is a second destination, so a card that
  // has one keeps its links as in-card buttons instead.
  const isCardLink = hasLink && !hasSupportLink;
  const hasHoverContent = hasAnyLink;
  React.useLayoutEffect(() => {
    setIsVideo(videoRef.current != null);
  }, []);
  React.useEffect(() => {
    if (!hasHoverContent || !cardRef.current || !hoverContentRef.current) return;
    const supportsHover = window.matchMedia("(hover: hover)").matches;
    const hoverContent = hoverContentRef.current;
    const items = hoverContent.querySelectorAll("[data-hover-item]");
    if (!supportsHover) {
      gsap.set(hoverContent, {
        height: "auto"
      });
      gsap.set(items, {
        opacity: 1
      });
      return;
    }
    const card = cardRef.current;
    gsap.set(hoverContent, {
      height: 0,
      overflow: "hidden"
    });
    gsap.set(items, {
      opacity: 0
    });
    const play = () => {
      activeTweenRef.current?.kill();
      const tl = gsap.timeline();
      tl.to(hoverContent, {
        height: "auto",
        duration: 0.55,
        ease: "power2.inOut"
      }).to(items, {
        opacity: 1,
        duration: 0.45,
        ease: "power2.out",
        stagger: 0.08
      }, "-=0.3");
      activeTweenRef.current = tl;
    };
    const reverse = () => {
      activeTweenRef.current?.kill();
      const tl = gsap.timeline();
      tl.to(items, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.in",
        stagger: {
          each: 0.04,
          from: "end"
        }
      }).to(hoverContent, {
        height: 0,
        duration: 0.45,
        ease: "power2.inOut"
      }, "-=0.15");
      activeTweenRef.current = tl;
    };
    card.addEventListener("mouseenter", play);
    card.addEventListener("mouseleave", reverse);
    card.addEventListener("focusin", play);
    card.addEventListener("focusout", reverse);
    return () => {
      card.removeEventListener("mouseenter", play);
      card.removeEventListener("mouseleave", reverse);
      card.removeEventListener("focusin", play);
      card.removeEventListener("focusout", reverse);
      activeTweenRef.current?.kill();
    };
  }, [hasHoverContent]);
  if (!media) {
    return null;
  }
  const cardContent = <_StyledDiv $_css={[{
    "aspectRatio": "340/370",
    "width": "100%"
  }, colSpan === 3 ? {
    "@media (min-width: 1024px)": {
      "aspectRatio": "1 / 1"
    }
  } : {
    "@media (min-width: 1024px)": {
      "aspectRatio": "auto",
      "minHeight": "54rem"
    }
  }]}>
      <_StyledDiv2 $_css2={[{
      "position": "absolute",
      "inset": "0"
    }, hasAnyLink && {
      "transitionProperty": "transform",
      "transitionDuration": "500ms",
      "transitionTimingFunction": "ease-out",
      "@media (hover: hover)": {
        ".group:hover &": {
          "--tw-scale-x": "1.05",
          "--tw-scale-y": "1.05",
          "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
        }
      }
    }]}>
        {media(videoRef)}
      </_StyledDiv2>
      {(title || description || hasHoverContent) &&
    // Full-card shade (design: "Content Image"), so the copy stays legible on any visual.
    <_StyledDiv3 />}
      {(title || description || hasHoverContent) && <_StyledDiv4 $_css3={[{
      "position": "absolute",
      "bottom": "0",
      "left": "0",
      "right": "0",
      "zIndex": "10",
      "display": "flex",
      "flexDirection": "column",
      "gap": "0.8rem",
      "padding": "1.6rem",
      "@media (min-width: 1024px)": {
        "padding": "2.4rem"
      }
    }, {
      "--tw-text-opacity": "1",
      "color": "rgb(255 255 255 / var(--tw-text-opacity))"
    }]}>
          {title && <_StyledHeadline type="h3" text={title} $_css4={[{
        "fontSize": "2.2rem",
        "lineHeight": "1.2",
        "fontWeight": "400"
      }, {
        // Scale the title to the card width (not the viewport) above lg so it
        // doesn't jump straight to 32px in narrow multi-card layouts (~1000-1400px),
        // where it would wrap and overflow the card.
        "@media (min-width: 1024px)": {
          fontSize: "clamp(2rem, 7cqi, 3.2rem)",
          lineHeight: 1.2,
          letterSpacing: "0.032rem"
        }
      }]} />}
          {description && <_StyledDiv5>
              <_StyledDiv6 $_css5={[{
          "fontSize": "1.6rem",
          "lineHeight": "1.5",
          "fontWeight": "400",
          "@media (min-width: 1024px)": {
            "maxWidth": "33ch"
          }
        }, {
          "@media (min-width: 1024px)": {
            fontSize: "clamp(1.4rem, 4cqi, 1.8rem)",
            lineHeight: 1.5,
            letterSpacing: "0.018rem"
          }
        }]}>
                {description}
              </_StyledDiv6>
            </_StyledDiv5>}
          {hasHoverContent && <div ref={hoverContentRef}>
              <_StyledDiv7 data-hover-item>
                {hasLink && (isCardLink ?
          // The card itself carries the href, so this is label-only: an
          // interactive element here would nest inside the card's anchor.
          <Button as="div" text={linkItem?.text} icon={icon} variant="secondary" /> : <Button as={Link} {...linkItem} icon={icon} variant="secondary" />)}
                {hasSupportLink && <Button as={Link} {...supportLinkItem} icon={supportIcon} variant="underline" />}
              </_StyledDiv7>
            </div>}
        </_StyledDiv4>}
      {(hasAnyLink || isVideo) && <_StyledDiv8 $_css6={[{
      "position": "absolute",
      "right": "0.8rem",
      "top": "0.8rem",
      "zIndex": "10",
      "@media (min-width: 1024px)": {
        "right": "1.6rem",
        "top": "1.6rem"
      }
    }]}>
          {isVideo ? <VideoControls videoRef={videoRef} size="small" variant="tertiary" /> : <_StyledDiv9 $_css7={[{
        ">*": {
          "transitionProperty": "color, background-color, border-color, text-decoration-color, fill, stroke",
          "transitionDuration": "250ms"
        },
        "@media (hover: hover)": {
          ".group:hover &>*": {
            "--tw-bg-opacity": "1 !important",
            "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity)) !important"
          }
        }
      }]}>
              <IconButton as="div" variant="primary" icon={{
          component: icon ?? supportIcon ?? Plus
        }} size="small" />
            </_StyledDiv9>}
        </_StyledDiv8>}
    </_StyledDiv>;

  // Establish a container above lg so the card text can scale to the card's
  // own width via cqi units (see Headline/RichText below).
  const containerCss = {
    "@media (min-width: 1024px)": {
      containerType: "inline-size"
    }
  };
  const innerCss = featured ? [{
    "position": "relative",
    "overflow": "hidden",
    "borderRadius": "4px",
    "@media (min-width: 1024px)": {
      "aspectRatio": "auto",
      "height": "100%"
    }
  }, containerCss] : [{
    "position": "relative",
    "overflow": "hidden",
    "borderRadius": "4px"
  }, containerCss];
  const colSpanCss = [colSpan === 12 ? {
    "gridColumn": "1 / -1",
    "minWidth": "0"
  } : colSpan === 8 ? {
    "gridColumn": "1 / -1",
    "minWidth": "0",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 8 / span 8"
    }
  } : colSpan === 6 ? {
    "gridColumn": "1 / -1",
    "minWidth": "0",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 6 / span 6"
    }
  } : colSpan === 4 ? {
    "gridColumn": "1 / -1",
    "minWidth": "0",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 4 / span 4"
    }
  } :
  // 4-card layout: 1-up on mobile, 2x2 from lg, 4-up above 1440px
  {
    "gridColumn": "1 / -1",
    "minWidth": "0",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 6 / span 6"
    },
    "@media (min-width: 1440px)": {
      "gridColumn": "span 3 / span 3"
    }
  }, featured && {
    "@media (min-width: 1024px)": {
      "gridRow": "span 2 / span 2"
    }
  }];
  const cardBody = <_StyledDiv10 ref={cardRef} className="group" $_css8={[innerCss, featured && {
    "@media (min-width: 1024px)": {
      "height": "100%"
    }
  }, isCardLink && {
    "cursor": "pointer"
  }]}>
      {cardContent}
    </_StyledDiv10>;
  return <_StyledDiv11 $_css9={colSpanCss}>
      {isCardLink ?
    // `block w-full text-left` keeps the card from collapsing when Link renders a
    // <button> (the request-quote href) instead of an <a>.
    <_StyledLink {...linkItem} $_css10={[{
      "display": "block",
      "width": "100%",
      "textAlign": "left"
    }, featured && {
      "@media (min-width: 1024px)": {
        "height": "100%"
      }
    }]}>
          {cardBody}
        </_StyledLink> : <_StyledDiv12 $_css11={[featured && {
      "@media (min-width: 1024px)": {
        "height": "100%"
      }
    }]}>{cardBody}</_StyledDiv12>}
    </_StyledDiv11>;
}
export function MediaGridContentImageCard(props: CardProps) {
  const {
    colSpan = 6,
    featured
  } = props;
  const {
    media,
    title,
    description,
    link,
    supportLink
  } = useFragment(mediaGridContentImageCardFragment, props);
  const rawLinkItem = takeFirst(link);
  const {
    linkText,
    ...rest
  } = rawLinkItem ?? {};
  // linkText is the card's custom CTA label; it now drives the label the card shows.
  const linkItem = linkText ? {
    ...rest,
    text: linkText
  } : rest;
  const supportLinkItem = takeFirst(supportLink);
  if (!hasItems(media)) {
    return null;
  }
  return <MediaGridContentImageCardView colSpan={colSpan} description={description ? <RichText text={description} /> : undefined} featured={featured} link={linkItem as MediaGridContentImageCardViewProps["link"] ?? undefined} media={videoRef => <Media {...takeFirst(media)} showCaption imageProps={{
    fit: "cover",
    sizes: colSpan === 12 ? "100vw" : colSpan === 8 ? "lg:66vw" : colSpan === 3 ? "lg:25vw" : colSpan === 4 ? "lg:33vw" : "lg:50vw"
  }} inlineVideoProps={{
    fit: "cover",
    videoRef: videoRef
  }} externalVideoProps={{
    fit: "cover",
    autoPlay: true,
    loop: true
  }} audioProps={{
    fit: "cover"
  }} />} supportLink={supportLinkItem as MediaGridContentImageCardViewProps["supportLink"] ?? undefined} title={title} />;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1ldmoy-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1ldmoy-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1ldmoy-2"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "inset": "0",
  "backgroundImage": "linear-gradient(180deg, rgba(0, 0, 0, 0) 40%, rgba(0, 0, 0, 0.6) 100%)"
});
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-1ldmoy-3"
})`${p => p.$_css3}`;
var _StyledHeadline = _styled5(Headline).withConfig({
  componentId: "sc-sf-1ldmoy-4"
})`${p => p.$_css4}`;
var _StyledDiv5 = _styled6("div").withConfig({
  componentId: "sc-sf-1ldmoy-5"
})({
  "paddingBottom": "0.4rem"
});
var _StyledDiv6 = _styled7("div").withConfig({
  componentId: "sc-sf-1ldmoy-6"
})`${p => p.$_css5}`;
var _StyledDiv7 = _styled8("div").withConfig({
  componentId: "sc-sf-1ldmoy-7"
})({
  "display": "flex",
  "alignItems": "center",
  "gap": "var(--spacing-lg)",
  "paddingTop": "0.4rem"
});
var _StyledDiv8 = _styled9("div").withConfig({
  componentId: "sc-sf-1ldmoy-8"
})`${p => p.$_css6}`;
var _StyledDiv9 = _styled10("div").withConfig({
  componentId: "sc-sf-1ldmoy-9"
})`${p => p.$_css7}`;
var _StyledDiv10 = _styled11("div").withConfig({
  componentId: "sc-sf-1ldmoy-10"
})`${p => p.$_css8}`;
var _StyledDiv11 = _styled12("div").withConfig({
  componentId: "sc-sf-1ldmoy-11"
})`${p => p.$_css9}`;
var _StyledLink = _styled13(Link).withConfig({
  componentId: "sc-sf-1ldmoy-12"
})`${p => p.$_css10}`;
var _StyledDiv12 = /*#__PURE__*/_styled14("div").withConfig({
  displayName: "content-image-card___StyledDiv12",
  componentId: "sc-r8i9u4-0"
})(["", ""], p => p.$_css11);